//Global imports
import { cilCloudDownload } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
    CButton,
    CCard,
    CCardBody,
    CCardFooter,
    CCardText,
    CCardTitle,
    CCol,
    CLink,
} from '@coreui/react';

//File imports
import { Language } from '../../../../language/English';
import { DocumentPojo } from '../../Api/DocumentPojo';
import { APP_LANGAUGE, BASE_URL, LANGAUGE_NAME } from '../../../../Constant';
import StorageServices from '../../../../services/Storage/StorageServices';
import Notification from '../../../../services/Notification/Notification';
import Logger from '../../../../services/Logger/Logger';
import DateUtility from '../../../../services/DateUtils/DateUtils';
import { useState } from 'react';
import { UtmParametersModal } from '../../../../components';

const DocumentCard = (props: { document: DocumentPojo; key: number }) => {
    const data = props.document;
    let metaInfo = data?.metaInfo ?? '{}';
    let isUtmAvailable =
        JSON.parse(metaInfo)?.utm &&
        Object.keys(JSON.parse(metaInfo)?.utm).length > 0;

    const notification = new Notification();
    const logger = new Logger();
    const dateUtility = new DateUtility();

    const [utmModalVisible, setUtmModalVisible] = useState(false);

    const getLangaugeBasedOnLangaugeId = (langaugeId: number): string => {
        const langKey = Object.keys(APP_LANGAUGE).find(
            (key) => APP_LANGAUGE[key] === langaugeId,
        );

        return langKey ? LANGAUGE_NAME[langKey] : '';
    };

    const handleDownloadDocument = async (
        uniqueId: string,
        documentTitle: string,
        isInvoice: boolean = false,
    ) => {
        try {
            const storageServices = new StorageServices();
            const userToken = storageServices.get('user');

            let apiUrl: string = BASE_URL;
            if (isInvoice) {
                apiUrl = apiUrl + '/document/invoice/download';
            } else {
                apiUrl = apiUrl + '/document/download';
            }
            const response = await fetch(apiUrl, {
                method: 'POST',
                body: JSON.stringify({ uniqueId }),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${JSON.parse(userToken ?? '').token}`,
                },
            });
            if (!response.ok) throw new Error('Failed to download');

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = isInvoice
                ? `Invoice_${documentTitle}.pdf`
                : `${documentTitle}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            notification.error(
                isInvoice
                    ? Language.ERROR_DOWNLOADING_INVOICE
                    : Language.ERROR_DOWNLOADING_DOCUMENT,
            );
            logger.error(error);
        }
    };

    return (
        <CCol xs={12} md={4} className="mb-4">
            <CCard className="border-0 shadow-sm rounded-2">
                <CCardBody>
                    <div className="d-flex justify-content-between align-items-start">
                        <CCardTitle
                            className="fs-3 text-truncate"
                            style={{ maxWidth: '100%' }}
                        >
                            {data?.translation?.title}
                        </CCardTitle>
                    </div>
                    <div className="d-flex justify-content-between">
                        <CCardText className="mb-0 fst-italic">
                            {dateUtility.formatUnixTimestampToDateString(
                                data.createdOn,
                            )}
                        </CCardText>
                    </div>
                    <div className="d-flex justify-content-between">
                        <CCardText className="mb-0">
                            <small>{Language.LANGAUGE}: </small>
                            <small className="fw-bold">
                                {getLangaugeBasedOnLangaugeId(
                                    data?.translation?.languageId,
                                )}
                            </small>
                        </CCardText>
                        <CCardText className="mb-0">
                            <small>{Language.PRICE}: </small>
                            <small className="fw-bold">
                                {data?.payment?.price}{' '}
                                <small className="fw-normal">
                                    {Language.INCLUDING_VAT}
                                </small>
                            </small>
                        </CCardText>
                    </div>

                    {JSON.parse(metaInfo)?.referer && (
                        <div className="d-flex justify-content-between mt-1">
                            <CCardText className="mb-0 text-break">
                                {Language.REFERRER}:{' '}
                                <span className="fw-bold">
                                    {JSON.parse(metaInfo)?.referer}
                                </span>
                            </CCardText>
                        </div>
                    )}

                    <div className="d-flex justify-content-between mt-1">
                        <CCardText className="mb-0">
                            {Language.GOOGLE_ADS}:{' '}
                            <span className="fw-bold">
                                {isUtmAvailable ? Language.YES : Language.NO}
                            </span>
                            {isUtmAvailable && (
                                <CLink
                                    href="#"
                                    className="link-primary small text-decoration-underline mx-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setUtmModalVisible(true);
                                    }}
                                >
                                    {Language.VIEW}
                                </CLink>
                            )}
                        </CCardText>
                    </div>

                    <CCardText className="fw-bold mt-1 mb-1">
                        {Language.CUSTOMER}
                    </CCardText>
                    <hr className="my-2" />

                    {data?.customer?.name && (
                        <CCardText className="mb-0">
                            <span>{data?.customer?.name}</span>
                        </CCardText>
                    )}

                    {data?.customer?.email && (
                        <CCardText className="mb-0">
                            <span>{data?.customer?.email}</span>
                        </CCardText>
                    )}

                    {data?.customer?.phoneNumber && (
                        <CCardText className="mb-0">
                            <span>{data?.customer?.phoneNumber}</span>
                        </CCardText>
                    )}
                </CCardBody>
                <CCardFooter className="bg-white">
                    <div className="d-flex gap-2">
                        <CButton
                            color="primary"
                            size="sm"
                            onClick={() =>
                                handleDownloadDocument(
                                    data.uniqueId,
                                    data.translation.title,
                                )
                            }
                        >
                            <CIcon icon={cilCloudDownload} className="me-1" />
                            {Language.DOWNLOAD_DOCUMENT}
                        </CButton>
                        <CButton
                            color="primary"
                            size="sm"
                            onClick={() =>
                                handleDownloadDocument(
                                    data.uniqueId,
                                    data.translation.title,
                                    true,
                                )
                            }
                        >
                            <CIcon icon={cilCloudDownload} className="me-1" />
                            {Language.DOWNLOAD_INVOICE}
                        </CButton>
                    </div>
                </CCardFooter>
            </CCard>

            <UtmParametersModal
                visible={utmModalVisible}
                onClose={() => setUtmModalVisible(false)}
                utmParams={JSON.parse(metaInfo)?.utm ?? {}}
            />
        </CCol>
    );
};

export default DocumentCard;
